import { XCircleIcon } from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { Input } from '@/components/form/Input';
import { OrganizationHeader } from '@/components/OrganizationHeader';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useRbacEnabled } from '@/hooks/useRbacEnabled';
import { StepProps } from '@/sections/customerManagement/components/modals/AddCustomerModal/AddCustomerModal.types';
import { ROLE_OPTIONS, type RoleName } from '@/utils/role.util';

/**
 * Step 5: Customer Users - Add customer team members to the account
 */
export const CustomerUsersStep: React.FC<StepProps> = ({
  formData,
  setFormData,
  isSubmitting,
}) => {
  const [collaboratorEmail, setCollaboratorEmail] = useState('');
  const enableRbac = useRbacEnabled();

  const handleAddCollaborator = () => {
    const email = collaboratorEmail.trim();

    // Validate email format
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Prevent adding Praetorian emails in customer step
    if (email.includes('@praetorian.com')) {
      toast.error('Praetorian users should be added in Step 4');
      return;
    }

    // Check for duplicates
    if (formData.collaborators.some(c => c.collaborator === email)) {
      toast.error('This email has already been added');
      return;
    }

    const newCollaborator = {
      collaborator: email,
      dateAdded: new Date().toISOString(),
      role: 'admin' as const,
    };
    setFormData(prev => ({
      ...prev,
      collaborators: [...prev.collaborators, newCollaborator],
    }));
    setCollaboratorEmail('');
  };

  const handleRemoveCollaborator = (email: string) => {
    setFormData(prev => ({
      ...prev,
      collaborators: prev.collaborators.filter(c => c.collaborator !== email),
    }));
  };

  const handleRoleChange = (email: string, role: RoleName) => {
    setFormData(prev => ({
      ...prev,
      collaborators: prev.collaborators.map(c =>
        c.collaborator === email ? { ...c, role } : c
      ),
    }));
  };

  const customerUsers = formData.collaborators.filter(
    c => !c.collaborator.endsWith('@praetorian.com')
  );

  return (
    <div className="flex flex-col gap-8">
      <OrganizationHeader
        displayName={formData.settings_display_name}
        profilePicture={formData.profile_picture}
        subtitle="Add customer team members (Optional)"
        className="mb-8"
      />

      <h3 className="text-chariot-text-primary mb-6 text-base font-medium">
        Customer Authorized Users (Optional)
      </h3>
      <p className="text-chariot-text-secondary mb-6 text-sm">
        Add customer team members who will have access to this account. You can
        skip this step and add users later.
      </p>

      <div className="space-y-6">
        {/* Add User Input */}
        <div className="flex gap-2">
          <div className="flex-1">
            <Input
              label=""
              name="collaborator_email"
              placeholder="user@customer-domain.com"
              value={collaboratorEmail}
              onChange={e => setCollaboratorEmail(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddCollaborator();
                }
              }}
              disabled={isSubmitting}
            />
          </div>
          <Button
            size="sm"
            onClick={handleAddCollaborator}
            disabled={
              isSubmitting ||
              !collaboratorEmail.trim().includes('@') ||
              collaboratorEmail.trim().includes('@praetorian.com')
            }
          >
            Add User
          </Button>
        </div>

        {/* Customer Users List */}
        {customerUsers.length > 0 ? (
          <div className="border-chariot-stroke rounded-md border">
            <div className="divide-chariot-stroke divide-y">
              {customerUsers.map((collab, index) => (
                <div
                  key={`${collab.collaborator}-${index}`}
                  className="flex items-center justify-between px-4 py-3"
                >
                  <div className="flex flex-col">
                    <span className="text-chariot-text-primary text-sm">
                      {collab.collaborator}
                    </span>
                    <span className="text-chariot-text-secondary text-xs">
                      Will be added on creation
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {enableRbac && (
                      <Select
                        value={collab.role}
                        onValueChange={role =>
                          handleRoleChange(
                            collab.collaborator,
                            role as RoleName
                          )
                        }
                        disabled={isSubmitting}
                      >
                        <SelectTrigger className="w-28" aria-label="Role">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLE_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <button
                      onClick={() =>
                        handleRemoveCollaborator(collab.collaborator)
                      }
                      disabled={isSubmitting}
                      className="text-chariot-stroke-critical hover:text-chariot-stroke-critical/80 p-1 transition-colors"
                      aria-label={`Remove ${collab.collaborator}`}
                    >
                      <XCircleIcon className="size-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-chariot-background-secondary border-chariot-stroke rounded-md border border-dashed p-8 text-center">
            <p className="text-chariot-text-secondary text-sm">
              No customer users added yet. Add customer team members to grant
              them access.
            </p>
          </div>
        )}

        {/* Final Note */}
        <div className="bg-chariot-background-tertiary border-chariot-stroke mt-6 rounded-lg border p-4">
          <p className="text-chariot-text-secondary text-sm">
            <span className="font-medium">Note:</span> Added users will receive
            an invitation email with instructions to access this customer
            account.
          </p>
        </div>
      </div>
    </div>
  );
};
